import { useEffect, useState, useRef, useLayoutEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Send, Armchair, Users, X } from "lucide-react";
import ChatName from "@/components/ChatName";
import FullBodyCharacter from "@/components/FullBodyCharacter";
import EmoteWheel from "@/components/EmoteWheel";
import EliteBountyBoard from "@/components/EliteBountyBoard";
import BountyContract from "@/components/BountyContract";
import DuelArena from "@/components/DuelArena";
import PlayerProfile from "@/components/PlayerProfile";
import FriendInbox from "@/components/FriendInbox";
import HubPartners from "@/components/HubPartners";
import { useTranslation } from "react-i18next";

const HUB_BG_URL = "https://customer-assets-39nsmqrw.emergentagent.net/job_63119e2a-b574-4db4-ac80-4785975abbed/artifacts/dccpccxa_pressi%20do%20ravencross.png";

// Deterministic banner color per guild id — hash into a saturated hsl so
// each guild reads as a distinct emblem tint under the chibi.
function guildBannerColor(guildId) {
  if (!guildId) return "#8c8c8c";
  const hash = String(guildId).split("").reduce(
    (h, c) => (h * 31 + c.charCodeAt(0)) >>> 0, 5381
  );
  const hue = hash % 360;
  return `hsl(${hue}, 65%, 55%)`;
}

// Isometric projection tuned to the hub background image.
// The 12×10 logical tile grid served by /api/hub/state is projected onto the
// diamond floor of the pixel-art artwork. Coordinates are eyeballed to align
// with the visible stone slabs.
const IMG_W = 720;
const IMG_H = 480;
const ORIGIN_X = IMG_W * 0.50;
const ORIGIN_Y = IMG_H * 0.30;
const T_DX = 28;
const T_DY = 14;
const T_DXY = -28;
const T_DYY = 14;

function iso(x, y) {
  return {
    left: ORIGIN_X + x * T_DX + y * T_DXY,
    top:  ORIGIN_Y + x * T_DY + y * T_DYY,
  };
}

export default function HubView({ ch, setCh }) {
  const { t } = useTranslation();
  const [state, setState] = useState(null);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const chatEndRef = useRef(null);
  // Track when each player last changed tile — powers the idle emote after 30s.
  const lastMoveRef = useRef(new Map());
  const [nowTick, setNowTick] = useState(Date.now());
  // Player profile modal — set to user_id to open, null to close.
  const [profileUid, setProfileUid] = useState(null);
  const [onlineOpen, setOnlineOpen] = useState(false);
  // Responsive scale — the isometric artwork is 720×480. On narrower viewports
  // we shrink it proportionally so the whole hall fits and chibi sprites can
  // never leak outside the mobile screen. useLayoutEffect fires before paint
  // so the very first render is already at the correct scale.
  const wrapRef = useRef(null);
  const [scale, setScale] = useState(1);
  useLayoutEffect(() => {
    const compute = () => {
      const el = wrapRef.current;
      if (!el) return;
      const w = el.getBoundingClientRect().width || el.clientWidth || IMG_W;
      const s = Math.min(1, w / IMG_W);
      if (s > 0) setScale(s);
    };
    compute();
    let ro = null;
    if (typeof ResizeObserver !== "undefined" && wrapRef.current) {
      ro = new ResizeObserver(compute);
      ro.observe(wrapRef.current);
    }
    window.addEventListener("resize", compute);
    return () => {
      window.removeEventListener("resize", compute);
      ro?.disconnect();
    };
  }, [state?.players?.length]);

  const refresh = async () => {
    try {
      const s = await api.hubState();
      // Track tile changes per player so we can detect idle-since durations.
      const now = Date.now();
      const map = lastMoveRef.current;
      const seen = new Set();
      for (const p of s.players || []) {
        seen.add(p.user_id);
        const prev = map.get(p.user_id);
        const posKey = `${p.x},${p.y},${p.sitting ? 1 : 0}`;
        if (!prev || prev.posKey !== posKey) {
          map.set(p.user_id, { posKey, at: now });
        }
      }
      for (const key of Array.from(map.keys())) {
        if (!seen.has(key)) map.delete(key);
      }
      setState(s);
    } catch (e) { /* silent */ }
  };

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 3000);
    // Tick every 5s so idle-emote flags flip once anyone hits 30s stationary.
    const idleTicker = setInterval(() => setNowTick(Date.now()), 5000);
    return () => {
      clearInterval(t);
      clearInterval(idleTicker);
    };
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [state?.chat?.length]);

  const moveTo = async (x, y) => {
    if (busy) return;
    setBusy(true);
    try {
      await api.hubMove(x, y);
      await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Move failed"); }
    finally { setBusy(false); }
  };

  const send = async () => {
    if (!msg.trim() || busy) return;
    setBusy(true);
    try {
      const res = await api.hubChat(msg.trim());
      if (res && res.ok === false && res.discipline) {
        const d = res.discipline;
        toast[d.muted ? "error" : "message"](d.reason || `Warning ${d.warning_count}/3`, { duration: 4000 });
      }
      setMsg("");
      await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Chat failed"); }
    finally { setBusy(false); }
  };

  const sit = async () => {
    if (busy) return;
    setBusy(true);
    try { await api.hubSit(); await refresh(); } catch (e) { /* noop */ }
    finally { setBusy(false); }
  };

  if (!state) return <div className="panel p-4 text-center text-[#666] text-sm italic">Entering the hub...</div>;

  const emotes = ["/bow", "/toast", "/laugh", "/wave", "/drink", "/kneel", "/pray", "/sigh"];

  return (
    <div className="relative isolate space-y-3" data-testid="hub-view">
      {/* Ravencross backdrop — transparent, only on the Hub. Fixed under
          the content so it never scrolls with the chat/tile map. */}
      <div
        aria-hidden="true"
        data-testid="hub-bg"
        className="pointer-events-none fixed inset-0 -z-10"
        style={{
          backgroundImage: `url(${HUB_BG_URL})`,
          backgroundSize: "cover",
          backgroundPosition: "center center",
          backgroundRepeat: "no-repeat",
          backgroundAttachment: "fixed",
          opacity: 0.18,
          filter: "saturate(0.85) contrast(1.05)",
        }}
      />
      <div className="panel p-2">
        <div className="flex items-center justify-between px-1 py-1 mb-2">
          <div className="font-heading text-sm tracking-[0.3em] uppercase text-[#E2DFD8]">{t("hub.commons")}</div>
          <div className="font-mono text-[10px] text-[#8c8c8c]">
            {state.players.length} soul{state.players.length !== 1 ? "s" : ""} in the hall
          </div>
        </div>

        {/* Isometric map — background image + full-body sprites. Wrapped in
            a responsive shell that scales the fixed-size 720×480 canvas down
            to the parent width so nothing overflows on mobile. */}
        <div
          ref={wrapRef}
          className="relative w-full overflow-hidden mx-auto"
          style={{
            maxWidth: IMG_W,
            height: IMG_H * scale,
          }}
        >
          <div
            className="relative"
            style={{
              width: IMG_W,
              height: IMG_H,
              backgroundImage: "url(/assets/hub-ravencross.jpg)",
              backgroundSize: "cover",
              backgroundPosition: "center",
              imageRendering: "pixelated",
              transform: `scale(${scale})`,
              transformOrigin: "top left",
            }}
            data-testid="hub-grid"
          >
            {/* Tile clickzones (invisible diamonds) */}
            {Array.from({ length: state.height }).map((_, y) =>
              Array.from({ length: state.width }).map((__, x) => {
                const { left, top } = iso(x, y);
                return (
                  <button
                    key={`${x}-${y}`}
                    onClick={() => moveTo(x, y)}
                    data-testid={`hub-tile-${x}-${y}`}
                    className="absolute cursor-pointer hover:bg-white/10 transition-colors"
                    style={{
                      left: left - 28,
                      top: top - 14,
                      width: 56,
                      height: 28,
                      clipPath: "polygon(50% 0, 100% 50%, 50% 100%, 0 50%)",
                      background: "transparent",
                      border: "none",
                    }}
                    aria-label={`Tile ${x},${y}`}
                  />
                );
              })
            )}

            {/* Players — sorted back-to-front for correct depth */}
            {[...state.players]
              .sort((a, b) => (a.y + a.x) - (b.y + b.x))
              .map(p => {
                const { left, top } = iso(p.x, p.y);
                const isMe = p.name === ch.name;
                const bodySize = 40;
                // Idle for >= 30s? Trigger a small sway emote.
                const lastMove = lastMoveRef.current.get(p.user_id)?.at || nowTick;
                const idleFor = nowTick - lastMove;
                const idle = !p.sitting && idleFor >= 30_000;
                // Vertical footprint = chibi height (64) + name (~14) + guild (~14).
                // Clamp within map so no piece of a chibi ever escapes the frame.
                const blockH = 64 + 14 + (p.guild?.tag ? 16 : 0);
                const rawTop = top - (bodySize * 1.6) + 12;
                const clampedTop = Math.max(2, Math.min(IMG_H - blockH - 2, rawTop));
                // Wrapper is 80px wide, anchored at chibi center - 40. Clamp
                // to keep the whole label frame inside the map.
                const anchorX = left; // chibi horizontal center in map coords
                const clampedAnchorX = Math.max(40 + 2, Math.min(IMG_W - 40 - 2, anchorX));
                const clampedLeft = clampedAnchorX - bodySize / 2;
                return (
                  <div
                    key={p.user_id}
                    data-testid={`hub-player-${p.user_id}`}
                    className="absolute flex flex-col items-center cursor-pointer"
                    onClick={() => setProfileUid(p.user_id)}
                    style={{
                      left: clampedLeft + bodySize / 2 - 40,
                      top: clampedTop,
                      width: 80,
                      zIndex: 100 + p.y + p.x,
                    }}
                  >
                    <FullBodyCharacter
                      size={bodySize}
                      appearance={p.appearance}
                      equipped={p.equipped}
                      cls={p.class}
                      cloak_color={p.cloak_color}
                      sitting={p.sitting}
                      idle={idle}
                      data_testid={`hub-fullbody-${p.user_id}`}
                    />
                    {p.emote?.symbol && (
                      <span
                        data-testid={`hub-emote-${p.user_id}`}
                        className="absolute"
                        style={{
                          left: "50%",
                          top: -14,
                          transform: "translate(-50%, -100%)",
                          fontSize: 22,
                          filter: "drop-shadow(0 0 6px rgba(229,195,74,0.5))",
                          animation: "hub-emote-pop 0.4s ease-out both, hub-emote-float 2.6s ease-in-out infinite",
                          zIndex: 200,
                        }}
                        aria-label={`emote ${p.emote.key}`}
                      >
                        {p.emote.symbol}
                      </span>
                    )}
                    <span
                      className="font-heading text-[10px] tracking-widest uppercase overflow-hidden text-ellipsis whitespace-nowrap max-w-full text-center px-1"
                      style={{
                        color: isMe ? "#E55A00" : "#E2DFD8",
                        textShadow: "0 0 4px #000, 0 0 2px #000",
                        marginTop: -4,
                      }}
                    >
                      {p.name}{p.sitting ? " · seated" : ""}
                    </span>
                    {p.guild?.tag && (
                      <span
                        data-testid={`hub-guild-${p.user_id}`}
                        title={p.guild.name}
                        className="font-heading text-[9px] tracking-widest uppercase px-1 whitespace-nowrap overflow-hidden text-ellipsis max-w-full text-center"
                        style={{
                          color: guildBannerColor(p.guild.id),
                          borderTop: `1px solid ${guildBannerColor(p.guild.id)}`,
                          borderBottom: `1px solid ${guildBannerColor(p.guild.id)}`,
                          background: `${guildBannerColor(p.guild.id)}22`,
                          textShadow: "0 0 3px #000",
                          marginTop: 2,
                          lineHeight: 1.4,
                        }}
                      >
                        [{p.guild.tag}]
                      </span>
                    )}
                  </div>
                );
              })}
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mt-3 items-center">
          <button
            data-testid="hub-sit"
            onClick={sit}
            disabled={busy}
            className="px-3 py-2 border border-[#B89C3A] text-[#E5C34A] font-heading text-xs tracking-widest uppercase flex items-center gap-2 disabled:opacity-30"
          >
            <Armchair className="w-3 h-3" strokeWidth={1.5} /> {ch.hub_sitting ? t("hub.stand") : t("hub.sit")}
          </button>
          <EmoteWheel />
          <FriendInbox onOpenProfile={setProfileUid} />
          <button data-testid="hub-online-button" onClick={() => setOnlineOpen(true)} className="px-3 py-2 border border-[#4A7BC7] text-[#4A7BC7] font-heading text-xs tracking-widest uppercase flex items-center gap-2"><Users className="w-3 h-3" strokeWidth={1.5} /> {t("hub.online")} ({state.players.length})</button>
          <span className="font-mono text-[10px] text-[#666] self-center">
            {t("hub.tapHint")}
          </span>
        </div>
      </div>

      <HubPartners />

      {onlineOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setOnlineOpen(false)} data-testid="hub-online-modal">
          <div className="w-full max-w-md max-h-[75vh] overflow-y-auto panel p-3" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-2 border-b border-[#262626] pb-2 mb-2">
              <Users className="w-4 h-4 text-[#4A7BC7]" />
              <div className="font-heading text-sm tracking-[0.25em] uppercase text-[#E2DFD8]">People Online</div>
              <button onClick={() => setOnlineOpen(false)} className="ml-auto text-[#666] hover:text-[#E2DFD8]"><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-1">
              {state.players.map(p => (
                <button key={p.user_id} onClick={() => { setOnlineOpen(false); setProfileUid(p.user_id); }} className="w-full text-left p-2 border border-[#262626] hover:border-[#B89C3A] flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-[#6E8B3D]" />
                  <div className="flex-1 min-w-0">
                    <div className="font-heading text-xs tracking-widest uppercase text-[#E2DFD8] truncate">{p.name}{p.user_id === ch.user_id ? " · You" : ""}</div>
                    <div className="font-mono text-[9px] uppercase text-[#8c8c8c]">Lv {p.level} · {p.class}</div>
                  </div>
                  {p.guild?.tag && <span className="font-mono text-[9px] text-[#B89C3A]">[{p.guild.tag}]</span>}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Player profile modal — opens when clicking a chibi */}
      {profileUid && (
        <PlayerProfile key={profileUid} userId={profileUid} onClose={() => setProfileUid(null)} />
      )}

      {/* Bounty Contract of the day (call to action before board) */}
      <BountyContract ch={ch} setCh={setCh} />
      {/* Duel Arena — challenge, accept, leaderboard */}
      <DuelArena ch={ch} setCh={setCh} players={state.players} />
      {/* Elite Bounty Board — community history of Named-Elite kills */}
      <EliteBountyBoard />

      {/* Chat */}
      <div className="panel" data-testid="hub-chat">
        <div className="px-3 py-2 border-b border-[#262626] font-heading text-xs tracking-[0.3em] uppercase text-[#8c8c8c]">
          Chat
        </div>
        <div className="max-h-52 overflow-y-auto p-3 space-y-1 text-sm">
          {(state.chat || []).length === 0 && (
            <p className="italic text-[#666] text-xs">Nobody has spoken yet.</p>
          )}
          {state.chat.map((c, i) => (
            <div key={i} className="font-body text-xs" data-testid={c.is_admin ? "chat-msg-admin" : undefined}>
              {c.kind === "emote" ? (
                <>
                  <ChatName name={c.name} isAdmin={c.is_admin} />{" "}
                  <span className="italic text-[#E5C34A]">{c.text}</span>
                </>
              ) : (
                <>
                  <ChatName name={c.name} isAdmin={c.is_admin} colon />{" "}
                  <span className="text-[#B0ADA5]">{c.text}</span>
                </>
              )}
            </div>
          ))}
          <div ref={chatEndRef} />
        </div>
        <div className="flex border-t border-[#262626]">
          <input
            data-testid="hub-chat-input"
            value={msg}
            onChange={(e) => setMsg(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder="Speak into the fog... (try /bow /toast /laugh /wave)"
            maxLength={200}
            className="flex-1 bg-transparent px-3 py-2 text-sm text-[#E2DFD8] outline-none placeholder-[#555]"
          />
          <button
            data-testid="hub-chat-send"
            onClick={send}
            disabled={busy || !msg.trim()}
            className="px-3 border-l border-[#262626] text-[#E55A00] hover:bg-[#1a1a1a] disabled:opacity-30"
          >
            <Send className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
        <div className="flex flex-wrap gap-1 p-2 border-t border-[#262626]" data-testid="hub-emote-bar">
          {emotes.map(e => (
            <button
              key={e}
              data-testid={`emote-${e.slice(1)}`}
              onClick={() => setMsg(e)}
              className="px-2 py-0.5 border border-[#262626] hover:border-[#B89C3A] text-[10px] font-mono text-[#8c8c8c] hover:text-[#E5C34A]"
            >
              {e}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
